import './SearchInput.scss';

import cn from 'classnames';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as SearchIcon } from '@/common/assets/icons/search.svg';
import Button from '@/common/components/atoms/Button/Button';
import TextField from '@/common/components/molecules/TextField/TextField';
import { ReactComponent as ResetIcon } from '@/ebat/assets/icons/close.svg';
import AutosuggestionsPanel from '@/ebat/components/blocks/ScanSelfService/SearchInput/AutosuggestionsPanel/AutosuggestionsPanel';
import FlashErrorMessage from '@/ebat/components/blocks/ScanSelfService/SearchInput/FlashErrorMessage/FlashErrorMessage';
import Loader from '@/ebat/components/blocks/ScanSelfService/SearchInput/Loader/Loader';
import {
  SearchInputDefaultProps,
  SearchInputPropTypes,
} from '@/ebat/components/blocks/ScanSelfService/SearchInput/SearchInput.proptypes';
import { useScanSelfServiceAutosuggestionsContext } from '@/ebat/contexts/ScanSelfServiceContext/ScanSelfServiceAutosuggestionsContext';
import { useScanSelfServiceContext } from '@/ebat/contexts/ScanSelfServiceContext/ScanSelfServiceContext';

const SearchInput = forwardRef(({ className, panelMaxHeight }, ref) => {
  const { t } = useTranslation();
  const prevHasProductRef = useRef();
  const {
    searchProduct,
    isLoading,
    hasProduct,
    error,
    clearSearchError,
  } = useScanSelfServiceContext();
  const {
    setAutosuggestionTerm,
    clearAutosuggestion,
  } = useScanSelfServiceAutosuggestionsContext();
  const inputRef = useRef();
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');

  const handleInputChange = (e) => {
    const { value } = e.target;
    setInputValue(value);
    setAutosuggestionTerm(value);
  };

  const handleSubmit = (value) => {
    searchProduct(value);
    setSearchValue(value);
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 13 && inputValue.trim() !== '') {
      handleSubmit(inputValue.trim());
      setInputValue('');
    }
  };

  const handleSearchIconClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleSubmit(inputValue.trim());
  };

  const handleBlur = () => {
    if (!hasProduct) {
      inputRef.current?.focus();
    }
  };

  const handleReset = useCallback(() => {
    clearAutosuggestion();
    setInputValue('');
    inputRef.current?.focus();
  }, [clearAutosuggestion]);

  useEffect(() => {
    if (
      (prevHasProductRef.current === undefined || prevHasProductRef.current) &&
      !hasProduct
    ) {
      inputRef.current?.focus();
    }
    if (!prevHasProductRef.current && hasProduct) {
      inputRef.current?.blur();
      clearAutosuggestion();
      setInputValue('');
    }

    prevHasProductRef.current = hasProduct;
  }, [clearAutosuggestion, hasProduct]);

  useEffect(() => {
    if (error) {
      clearSearchError();
      handleReset();
    }
  }, [clearSearchError, error, handleReset]);

  return (
    <>
      <div
        ref={ref}
        className={cn('scan-self-service-search-input', className)}
      >
        <TextField
          ref={inputRef}
          placeholder={t(
            'ebat:entities:scan_self_service.search.input-placeholder',
          )}
          className="scan-self-service-search-input__input"
          context="search-input"
          icon={
            <div>
              <Button
                className={cn('reset-icon', {
                  'reset-icon--hidden': inputValue.trim() === '',
                })}
                context="reset"
                onClick={handleReset}
              >
                <ResetIcon className="icon" />
              </Button>
              <Button
                className="search-icon"
                context="search-save"
                onClick={handleSearchIconClick}
                disabled={inputValue.trim() === ''}
              >
                <SearchIcon className="icon" />
              </Button>
            </div>
          }
          value={inputValue}
          disabled={hasProduct}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
        />
        {isLoading && (
          <Loader className="scan-self-service-search-input__loader" />
        )}
        <AutosuggestionsPanel
          className="scan-self-service-search-input__autosuggestion"
          panelMaxHeight={panelMaxHeight}
        />
      </div>
      <FlashErrorMessage
        className="scan-self-service-search-input__flash-message"
        searchValue={searchValue}
      />
    </>
  );
});

SearchInput.defaultProps = {
  ...SearchInputDefaultProps,
};

SearchInput.propTypes = {
  ...SearchInputPropTypes,
};

export default SearchInput;
